import { useCallback, useMemo, useState } from 'react';
import {
  getModelsAPI,
  saveModelAPI,
  editModelAPI,
  deleteModelAPI,
  deleteModelVersionAPI,
  getProjectsDDAPI,
  handleDownloadImagesAPI,
  getTrainingProgressAPI,
  getTrainingLogsAPI,
  downloadTrainedModelAPI,
} from '../api/modelManagementAPI';
import useModelManagementStore from '../store/useModelManagementStore';
import { useSwal } from '../../../../components/Notifications/swal/SwalCom';
import { getLoggedInOwnerId } from '../../../../utils/auth';

const useModelManagementHook = () => {
  const {
    setProjects,
    setModelName,
    setModelDetailInputField,
    setModelInfo,
    setModelDataInfo,
    setIsModalOpen,
    setProjectDD,
    setProjectId,
    setModelType,
    resetModelForm,
  } = useModelManagementStore();
  const { swalSuccess, swalFail } = useSwal();
  const [downloadingModelId, setDownloadingModelId] = useState(null);

  const buildOwnerParams = useCallback((params = {}) => {
    const ownerId = getLoggedInOwnerId();
    if (!ownerId) {
      console.warn('Owner ID not found in storage.');
      return null;
    }
    return { owner_id: ownerId, ...params };
  }, []);

  const ensureFormDataOwner = useCallback((formData) => {
    if (!(formData instanceof FormData)) return formData;
    const ownerId = getLoggedInOwnerId();
    if (ownerId && !formData.get('owner_id')) {
      formData.append('owner_id', ownerId);
    }
    return formData;
  }, []);

  const alertMessage = useCallback(
    (message, isError = false) => {
      if (!message) return;
      if (isError) {
        swalFail('Oops...', message);
      } else {
        swalSuccess('Notice', message);
      }
    },
    [swalFail, swalSuccess],
  );

  const handleGetModels = useCallback(
    async (params = {}) => {
      try {
        const paramWithOwner = buildOwnerParams(params);
        if (!paramWithOwner) return;
        const response = await getModelsAPI(paramWithOwner);
        const payload = Array.isArray(response?.data) ? response.data : [];
        setProjects(payload);
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    },
    [buildOwnerParams, setProjects],
  );

  const handleGetProjectsDD = useCallback(async () => {
    try {
      const ownerParams = buildOwnerParams();
      if (!ownerParams) return;
      const response = await getProjectsDDAPI(ownerParams);
      const payload = Array.isArray(response?.data) ? response.data : [];
      setProjectDD(payload);
    } catch (error) {
      console.error('Error fetching project dropdown data:', error);
    }
  }, [buildOwnerParams, setProjectDD]);

  const handleOpenModal = useCallback(
    (mode) => {
      if (mode === 'new') {
        resetModelForm();
      }
      setIsModalOpen(true);
    },
    [resetModelForm, setIsModalOpen],
  );

  const handleDeleteModel = useCallback(
    async (model) => {
      if (!model?.model_id) return false;
      try {
        await deleteModelAPI({ params: { id: model.model_id } });
        const projectId = model?.project_id || model?.projectId;
        await handleGetModels(projectId ? { project_id: projectId } : undefined);
        return true;
      } catch (error) {
        console.error('Error deleting model:', error);
        return false;
      }
    },
    [handleGetModels],
  );

  const handleDeleteModelVersion = useCallback(
    async ({ model, verion_id: rawVersionId, version_id }) => {
      const versionId = rawVersionId ?? version_id;
      if (!versionId) return false;
      try {
        await deleteModelVersionAPI({
          params: {
            id: versionId,
            model_type: model?.model_type || 'classification',
          },
        });
        const projectId = model?.project_id || model?.projectId;
        await handleGetModels(projectId ? { project_id: projectId } : undefined);
        return true;
      } catch (error) {
        console.error('Error deleting model version:', error);
        return false;
      }
    },
    [handleGetModels],
  );

  const handleEditModel = useCallback(
    async (formData) => {
      if (!(formData instanceof FormData)) {
        throw new Error('handleEditModel expects FormData');
      }
      try {
        ensureFormDataOwner(formData);
        const response = await editModelAPI(formData);
        const message = response?.data?.message;
        if (message === 'success') {
          const projectId = formData.get('project_id');
          await handleGetModels({ project_id: projectId });
          alertMessage('Successfully saved');
          resetModelForm();
          setIsModalOpen(false);
          return { success: true };
        }
        return {
          success: false,
          message: message || 'Failed to save model',
        };
      } catch (error) {
        console.error('Error editing model:', error);
        return {
          success: false,
          message: 'Something went wrong while saving. Please try again.',
        };
      }
    },
    [alertMessage, ensureFormDataOwner, handleGetModels, resetModelForm, setIsModalOpen],
  );

  const handleSaveModel = useCallback(
    async (formData) => {
      if (!(formData instanceof FormData)) {
        throw new Error('handleSaveModel expects FormData');
      }
      try {
        ensureFormDataOwner(formData);
        const response = await saveModelAPI(formData);
        const message = response?.data?.message;
        if (message === 'success') {
          const savedModel = response?.data?.data || null;
          setModelInfo(savedModel);
          const projectId = savedModel?.projectId || savedModel?.project_id || formData.get('project_id');
          if (projectId) {
            await handleGetModels({ project_id: projectId });
          } else {
            await handleGetModels();
          }
          alertMessage('Successfully saved');
          resetModelForm();
          setIsModalOpen(false);
          return { success: true, data: savedModel };
        }
        return {
          success: false,
          message: message || 'Failed to save model',
        };
      } catch (error) {
        console.error('Error saving model:', error);
        return {
          success: false,
          message: 'Something went wrong while saving. Please try again.',
        };
      }
    },
    [alertMessage, ensureFormDataOwner, handleGetModels, resetModelForm, setIsModalOpen, setModelInfo],
  );

  const handleDownloadImageData = useCallback(async (params) => {
    try {
      const response = await handleDownloadImagesAPI(params);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `images_${params.model_data_info_id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { status: 'success' };
    } catch (error) {
      console.error('Download failed:', error);
      alertMessage(`Download failed: ${error.message}`, true);
      return { status: 'error', message: error.message };
    }
  }, [alertMessage]);

  const handleResetAddPopup = useCallback(() => {
    resetModelForm();
  }, [resetModelForm]);

  const handleGetTrainingProgress = useCallback(async ({ modelType, modelDataInfoId, jobId }) => {
    try {
      const response = await getTrainingProgressAPI({
        modelType,
        modelDataInfoId,
        jobId,
      });
      return response?.data;
    } catch (error) {
      console.error('Error fetching training progress:', error);
      throw error;
    }
  }, []);

  // Replays the log lines Redis still holds for this job. The WebSocket only
  // carries lines published while it is open, so without this seed a panel
  // opened after training started (or after it already failed) renders empty.
  const handleGetTrainingLogs = useCallback(async ({ modelType, modelDataInfoId, jobId, limit = 200 }) => {
    try {
      const response = await getTrainingLogsAPI({
        modelType,
        modelDataInfoId,
        jobId,
        limit,
      });
      return response?.data;
    } catch (error) {
      console.error('Error fetching training logs:', error);
      throw error;
    }
  }, []);

  const handleDownloadTrainedModel = useCallback(async ({ model_type, model_data_info_id, owner_id }) => {
    // Set loading state
    setDownloadingModelId(model_data_info_id);
    try {
      const params = {
        modelType: model_type,
        modelDataInfoId: model_data_info_id,
      };
      if (owner_id) params.ownerId = owner_id;
      const response = await downloadTrainedModelAPI(params);
      const contentDisposition = response.headers?.["content-disposition"];
      // Default filename for zip file containing full training output
      let filename = `training_output_${model_data_info_id}_${model_type}.zip`;
      if (contentDisposition && contentDisposition.includes("filename=")) {
        filename = contentDisposition.split("filename=")[1].replace(/"/g, "");
      }
      // Use application/zip for zip files
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download model failed:", error);
      alertMessage(`Download model failed: ${error.message}`, true);
    } finally {
      // Clear loading state
      setDownloadingModelId(null);
    }
  }, [alertMessage]);

  const handleDownloadTrainingConfig = useCallback(({ version, model_type }) => {
    const params = version?.trainingParams || {};
    const lines = [
      `Model Type: ${model_type || "-"}`,
      `Model Variant: ${params.modelName || "-"}`,
      `Epochs: ${params.epochs || "-"}`,
      `Image Size: ${params.imgsz || "-"}`,
    ];
    alertMessage(lines.join("\n"), false);
  }, [alertMessage]);

  const handler = useMemo(
    () => ({
      handleGetModels,
      handleDeleteModel,
      handleDeleteModelVersion,
      handleEditModel,
      handleSaveModel,
      handleResetAddPopup,
      handleOpenModal,
      handleGetProjectsDD,
      handleDownloadImageData,
      handleGetTrainingProgress,
      handleGetTrainingLogs,
      handleDownloadTrainedModel,
      handleDownloadTrainingConfig,
      setProjectId,
      setModelInfo,
      setModelDataInfo,
      setModelName,
      setModelDetailInputField,
      setModelType,
    }),
    [
      handleDeleteModel,
      handleDeleteModelVersion,
      handleDownloadImageData,
      handleGetTrainingProgress,
      handleGetTrainingLogs,
      handleDownloadTrainedModel,
      handleDownloadTrainingConfig,
      handleEditModel,
      handleGetModels,
      handleGetProjectsDD,
      handleOpenModal,
      handleResetAddPopup,
      handleSaveModel,
      setModelDataInfo,
      setModelDetailInputField,
      setModelInfo,
      setModelName,
      setModelType,
      setProjectId,
    ],
  );

  return { handler, downloadingModelId };
};

export default useModelManagementHook;